import { useEffect } from 'react';

export interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  jsonLd?: object | object[];
  noIndex?: boolean;
  /** Use 'article' for blog posts and tutorials */
  ogType?: 'website' | 'article';
  /** ISO date string — used for article:published_time */
  articlePublishedTime?: string;
  /** ISO date string — used for article:modified_time */
  articleModifiedTime?: string;
  /** Article author name */
  articleAuthor?: string;
  /** Article section / category */
  articleSection?: string;
  /** Article tags */
  articleTags?: string[];
}

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function injectJsonLd(data: object | object[], id: string) {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = id;
  script.textContent = JSON.stringify(Array.isArray(data) ? data : data);
  document.head.appendChild(script);
}

const DEFAULT_OG_IMAGE =
  'https://readdy.ai/api/search-image?query=professional%20VPS%20server%20hosting%20company%20banner%20dark%20background%20glowing%20server%20racks%20data%20center%20technology%20high%20performance%20computing%20modern%20minimalist%20black%20teal%20color%20scheme%20premium%20quality%20wide%20horizontal%20brand%20banner%20social%20media&width=1200&height=630&seq=musthost-og-default-001&orientation=landscape';

/** Removes an optional meta property tag created dynamically */
function removeMeta(name: string, attr: 'name' | 'property' = 'property') {
  const el = document.querySelector(`meta[${attr}="${name}"]`);
  if (el) el.remove();
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  jsonLd,
  noIndex,
  ogType = 'website',
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleSection,
  articleTags,
}: SEOProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://musthost.com.br';
    const canonicalUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
    const image = ogImage || DEFAULT_OG_IMAGE;

    // Title
    document.title = title;

    // Basic meta
    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('last-modified', new Date().toISOString());
    setMeta('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Canonical (skip for noIndex pages)
    if (!noIndex) {
      setLink('canonical', canonicalUrl);
    }

    // ── Open Graph ──────────────────────────────────────
    setMeta('og:type', ogType, 'property');
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:image', image, 'property');
    setMeta('og:image:secure_url', image, 'property');
    setMeta('og:image:width', '1200', 'property');
    setMeta('og:image:height', '630', 'property');
    setMeta('og:image:alt', title, 'property');
    setMeta('og:image:type', 'image/jpeg', 'property');
    setMeta('og:site_name', 'musthost', 'property');
    setMeta('og:locale', 'pt_BR', 'property');

    // ── Article-specific Open Graph ──────────────────────
    if (ogType === 'article') {
      if (articlePublishedTime)
        setMeta('article:published_time', new Date(articlePublishedTime).toISOString(), 'property');
      if (articleModifiedTime)
        setMeta('article:modified_time', new Date(articleModifiedTime).toISOString(), 'property');
      else if (articlePublishedTime)
        setMeta('article:modified_time', new Date(articlePublishedTime).toISOString(), 'property');
      if (articleAuthor)
        setMeta('article:author', articleAuthor, 'property');
      if (articleSection)
        setMeta('article:section', articleSection, 'property');
      // article:tag — one meta per tag
      const prevTags = document.querySelectorAll('meta[property="article:tag"]');
      prevTags.forEach((el) => el.remove());
      if (articleTags && articleTags.length > 0) {
        articleTags.forEach((tag) => {
          const el = document.createElement('meta');
          el.setAttribute('property', 'article:tag');
          el.setAttribute('content', tag);
          document.head.appendChild(el);
        });
      }
    } else {
      // Clean up article metas if switching back to website type
      removeMeta('article:published_time');
      removeMeta('article:modified_time');
      removeMeta('article:author');
      removeMeta('article:section');
      document.querySelectorAll('meta[property="article:tag"]').forEach((el) => el.remove());
    }

    // ── Twitter Card ──────────────────────────────────────
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@musthost');
    setMeta('twitter:creator', '@musthost');
    setMeta('twitter:domain', 'musthost.com.br');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);
    setMeta('twitter:image:alt', title);

    // ── Geo ───────────────────────────────────────────────
    setMeta('geo.region', 'BR-CE');
    setMeta('geo.placename', 'Fortaleza, Ceará, Brasil');
    setMeta('geo.position', '-3.7327;-38.5270');
    setMeta('ICBM', '-3.7327, -38.5270');

    // ── JSON-LD ───────────────────────────────────────────
    if (jsonLd && !noIndex) injectJsonLd(jsonLd, 'page-jsonld');

    return () => {
      const el = document.getElementById('page-jsonld');
      if (el) el.remove();
      // Clean up article-specific meta tags on unmount
      ['article:published_time', 'article:modified_time', 'article:author', 'article:section'].forEach((prop) => {
        const meta = document.querySelector(`meta[property="${prop}"]`);
        if (meta) meta.remove();
      });
      document.querySelectorAll('meta[property="article:tag"]').forEach((e) => e.remove());
    };
  }, [title, description, keywords, canonical, ogImage, jsonLd, noIndex, ogType,
      articlePublishedTime, articleModifiedTime, articleAuthor, articleSection, articleTags]);
}
